/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import type { TFunction } from 'i18next';
import { U2000FormStateEnumState } from '../../../../u2000Core/reducer/formReducer/U2000FormState';
import type { U2000FormStateHandlers } from '../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import { NS } from '../../../U3206';
import { useU3206Context } from '../../../U3206Context';
import type { U3206AiUserGroupFormRow, U3206GroupProviderLink } from '../../../U3206Service';
import {
  u3206FormatQuotaInputValue,
  u3206ParseQuotaInput,
} from '../../../utils/u3206ParseQuotaInput';

interface U3206ProvidersTabProps {
  formStateHandlers: U2000FormStateHandlers<U3206AiUserGroupFormRow>;
  t: TFunction;
}

function U3206ProvidersTab({ formStateHandlers, t }: U3206ProvidersTabProps) {
  const { initLU } = useU3206Context();
  const data = formStateHandlers.getData();
  const readonly = formStateHandlers.getFormState() === U2000FormStateEnumState.READ;
  const providers = data?.providers ?? [];

  if (initLU.providers.length === 0) {
    return <p className="text-sm text-muted-foreground">{t('lblNoProviders', { ns: NS })}</p>;
  }

  const isSelected = (providerId: string) => providers.some((p) => p.providerId === providerId);

  const getLink = (providerId: string): U3206GroupProviderLink | undefined =>
    providers.find((p) => p.providerId === providerId);

  const setProviders = (next: U3206GroupProviderLink[]) => {
    formStateHandlers.onChange?.(
      { target: { id: 'providers', value: next } } as unknown as React.ChangeEvent<HTMLInputElement>,
      next,
    );
  };

  const toggleProvider = (providerId: string, code: string, name: string, checked: boolean) => {
    if (readonly) return;
    if (checked) {
      setProviders([
        ...providers,
        {
          providerId,
          code,
          name,
          allowPersonalMissions: false,
          groupQuotaMonthlyTokens: null,
          userQuotaMonthlyTokens: null,
        },
      ]);
    } else {
      setProviders(providers.filter((p) => p.providerId !== providerId));
    }
  };

  const toggleAllowPersonal = (providerId: string, checked: boolean) => {
    if (readonly) return;
    setProviders(
      providers.map((p) => (p.providerId === providerId ? { ...p, allowPersonalMissions: checked } : p)),
    );
  };

  const updateQuota = (
    providerId: string,
    field: 'groupQuotaMonthlyTokens' | 'userQuotaMonthlyTokens',
    rawValue: string,
  ) => {
    if (readonly) return;
    const parsed = u3206ParseQuotaInput(rawValue);
    if (parsed === undefined) return;
    setProviders(
      providers.map((p) => (p.providerId === providerId ? { ...p, [field]: parsed } : p)),
    );
  };

  return (
    <div className="space-y-2">
      {initLU.providers.map((provider) => {
        const selected = isSelected(provider.id);
        const link = getLink(provider.id);
        const rowId = `provider-${provider.id}`;
        return (
          <div
            key={provider.id}
            className="flex flex-col gap-2 rounded-lg border border-border p-3"
          >
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <label className="flex items-center gap-2 text-sm font-medium">
                <input
                  id={rowId}
                  type="checkbox"
                  checked={selected}
                  disabled={readonly}
                  onChange={(e) => toggleProvider(provider.id, provider.code, provider.name, e.target.checked)}
                  className="h-4 w-4 rounded-sm border-gray-300"
                />
                <span>
                  {provider.code} — {provider.name}
                </span>
              </label>
              {selected ? (
                <label
                  className="flex items-center gap-2 text-xs text-muted-foreground"
                  title={t('allowPersonalMissionsTooltip', { ns: NS })}
                >
                  <input
                    type="checkbox"
                    checked={link?.allowPersonalMissions ?? false}
                    disabled={readonly}
                    onChange={(e) => toggleAllowPersonal(provider.id, e.target.checked)}
                    className="h-4 w-4 rounded-sm border-gray-300"
                  />
                  <span>{t('allowPersonalMissions', { ns: NS })}</span>
                </label>
              ) : null}
            </div>
            {selected ? (
              <div className="grid gap-3 border-t border-border pt-3 sm:grid-cols-2">
                <label className="flex flex-col gap-1 text-xs">
                  <span className="font-medium text-foreground">{t('groupQuotaMonthlyTokens', { ns: NS })}</span>
                  <input
                    type="number"
                    min={0}
                    step={1}
                    disabled={readonly}
                    placeholder={t('quotaUnlimitedHint', { ns: NS })}
                    title={t('groupQuotaTooltip', { ns: NS })}
                    value={u3206FormatQuotaInputValue(link?.groupQuotaMonthlyTokens)}
                    onChange={(e) => updateQuota(provider.id, 'groupQuotaMonthlyTokens', e.target.value)}
                    className="rounded-md border border-border bg-background px-2 py-1.5 text-sm disabled:opacity-50"
                  />
                </label>
                <label className="flex flex-col gap-1 text-xs">
                  <span className="font-medium text-foreground">{t('userQuotaMonthlyTokens', { ns: NS })}</span>
                  <input
                    type="number"
                    min={0}
                    step={1}
                    disabled={readonly}
                    placeholder={t('quotaUnlimitedHint', { ns: NS })}
                    value={u3206FormatQuotaInputValue(link?.userQuotaMonthlyTokens)}
                    onChange={(e) => updateQuota(provider.id, 'userQuotaMonthlyTokens', e.target.value)}
                    className="rounded-md border border-border bg-background px-2 py-1.5 text-sm disabled:opacity-50"
                  />
                </label>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export default U3206ProvidersTab;
